import React, {useCallback, useMemo} from "react";
import {useQuery} from "@tanstack/react-query";
import {Combobox, ComboboxOptionProps, FilterFn, InputLabel, SelectOptionProps} from "peggirkit";
import {FormattedMessage, useIntl} from "react-intl";
import {ProductReference} from "@/data/entities/product";
import ComboboxOption from "@widgets/featuredProducts/editor/card/productSearch/ComboboxOption";
import {api} from "@/data/api";

type Props = {
    initialLoading: boolean,
    restaurantId: number,
    value: number | null,
    onChange: (selected: ProductReference) => void,
    error: string | undefined,
};

const ProductReferenceCombobox = ({restaurantId, initialLoading, value, onChange, error}: Props) => {
    const intl = useIntl();
    const {data: productList, isLoading} = useQuery({
        queryKey: ["products", restaurantId],
        queryFn: () => api.product.listProducts(restaurantId),
    });
    const options: ComboboxOptionProps<ProductReference>[] = useMemo(
        () => !productList
            ? []
            : productList.map(p => ({
                value: p.id,
                displayValue: p.name,
                componentProps: p,
            })),
        [productList]);
    const selected: ComboboxOptionProps<ProductReference> | null = useMemo(
        () => {
            const selected = options.filter(o => o.value === value)
            if (selected.length === 0)
                return null

            return selected[0]
        },
        [options, value]);

    // todo - is value !== null && selected is undefined, then product doesn't exist (anymore)

    const filterFn: FilterFn<ProductReference> = useCallback(
        (query: string, options: SelectOptionProps<ProductReference>[]) => {
            const result = options.filter((o: SelectOptionProps<ProductReference>) =>
                o.displayValue.toString().toLowerCase().includes(query.toLowerCase()),
            );

            if (result.length === 0) {
                return [{
                    value: -1,
                    displayValue: intl.formatMessage({id: "noResults"}),
                    disabled: true,
                }];
            }

            return result;
        }, [intl]);

    return (
        <Combobox<ProductReference>
            label={
                <InputLabel hint={<FormattedMessage id={"required"}/>}>
                    <FormattedMessage id={"product"}/>
                </InputLabel>
            }
            disabled={isLoading || initialLoading}
            textFieldProps={{
                placeholder: intl.formatMessage({id: "searchAProduct"}),
            }}
            options={options}
            onChange={value => onChange(value.componentProps as ProductReference)}
            value={selected}
            filterFn={filterFn}
            optionComponent={ComboboxOption}
            tip={intl.formatMessage({id: "featuredProductProductTip"})}
            error={error}

        />
    );
};

export default ProductReferenceCombobox;